import cacheManager from "../store/CacheManager";
import store from "../store/store";
import { SharedBox, SharedBoxAttachment, SharedBoxMessage, SharedBoxRecipient, SharedBoxRecipientType } from "../types/SharedBox";

const PRIMITIVE_FIELDS = [
    'id', 'label',
    'canReplyAttachment', 'canReplyText', 'status',
    'createdAt', 'updatedAt', 'createdBy', 'updatedBy'
];

const ArrayToHash = (key: string, items: Array<any>) => (
    (Array.isArray(items) ? items : []).reduce((memo, item) => {
        memo[item[key]] = item;
        return memo;
    }, {})
);

const sha256 = async (str: string) => {
    const buffer = new TextEncoder().encode(str);
    const hash = await crypto.subtle.digest('SHA-256', buffer);
    return Array.prototype.map.call(new Uint8Array(hash), (x) => (('00' + x.toString(16)).slice(-2))).join('');
};

export const ToHumanFileSize = (sizeInput: string|number) => {
    const size = (typeof sizeInput === 'string' ? parseInt(sizeInput, 10) : sizeInput);
    var i = Math.floor( Math.log(size) / Math.log(1024) );
    const pw = Math.pow(1024, i);
    return (size / pw ).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
};

export const MessagesSmartUpdate = async (
    currentMessages: Array<SharedBoxMessage>,
    newMessages: Array<SharedBoxMessage>,
    recipientDictionary?: {[key: string]: SharedBoxRecipient},
    isNewMessage: boolean = false
): Promise<Array<SharedBoxMessage>> => {
    const messagesHashIds = ArrayToHash('id', currentMessages);
    const copyCurrentMessages = [...currentMessages];
    for (const message of newMessages) {
        const recipientId = message.recipientId;
        if (recipientId && recipientDictionary && recipientDictionary[recipientId]) {
            message.recipient = recipientDictionary[recipientId];
        }
        const messageHash = await sha256(JSON.stringify(message));
        const alreadyExistMessage = messagesHashIds[message.id];
        if (alreadyExistMessage && alreadyExistMessage.hash != messageHash) {
            const mIndex = copyCurrentMessages.findIndex((m) => m.id === message.id);
            copyCurrentMessages[mIndex] = message;
        }
        message.hash = messageHash;
    }
    const messagesHash = ArrayToHash('hash', copyCurrentMessages);
    const newMessagesHash = ArrayToHash('hash', newMessages);
    const missingKeys = Object.keys(newMessagesHash).filter((key) => !messagesHash[key] && !messagesHashIds[newMessagesHash[key].id]);
    const mergedMessages = copyCurrentMessages.concat(missingKeys.map((key) => {
        const msg = newMessagesHash[key];
        msg.isNewMessage = (isNewMessage === true);
        return msg;
    }));
    const sortMessages = (a: SharedBoxMessage, b: SharedBoxMessage) => (
        (new Date(a.createdAt)).getTime() - (new Date(b.createdAt)).getTime()
    );
    return mergedMessages.sort(sortMessages);
};

export const RecipientsSmartUpdate = (
    currentRecipients: Array<SharedBoxRecipient>,
    newRecipients: Array<SharedBoxRecipient>
): Array<SharedBoxRecipient> => {
    const recipientsHash = ArrayToHash('id', currentRecipients);
    const newRecipientsHash = ArrayToHash('id', newRecipients);
    const missingKeys = Object.keys(newRecipientsHash).filter((key) => !recipientsHash[key]);
    const mergedRecipients = currentRecipients.concat(missingKeys.map((key) => newRecipientsHash[key]));
    mergedRecipients.forEach((recipient) => {
        if (!recipient.aliasName) {
            recipient.aliasName = recipient.value;
            if (recipient.type === SharedBoxRecipientType.AMCUserId) {
                const user = cacheManager.get('users', recipient.value);
                recipient.aliasName = (user ? `${user.firstname} ${user.lastname}` : recipient.value);
            }
        }
        return recipient;
    });
    return mergedRecipients;
};

export const SharedBoxSmartUpdate = async (
    currentSharedBox: Partial<SharedBox>,
    newSharedBox: SharedBox,
    isNew: boolean = true
) => {
    const mergedSharedBox: SharedBox = ({ ...currentSharedBox }) as SharedBox;
    const newSharedBoxKeys = Object.keys(newSharedBox);
    PRIMITIVE_FIELDS.reduce((memo: SharedBox, field: string) => {
        if (newSharedBoxKeys.includes(field)) {
            (memo as any)[field] = (newSharedBox as any)[field];
        }
        return memo;
    }, mergedSharedBox);
    const messages = (Array.isArray(currentSharedBox.messages) ? currentSharedBox.messages : []);
    const recipients = (Array.isArray(currentSharedBox.recipients) ? currentSharedBox.recipients : []);
    mergedSharedBox.recipients = RecipientsSmartUpdate(recipients, newSharedBox.recipients);
    mergedSharedBox.recipientDictionary = ArrayToHash('id', mergedSharedBox.recipients);
    mergedSharedBox.messages = await MessagesSmartUpdate(messages, newSharedBox.messages, mergedSharedBox.recipientDictionary, isNew);
    const newSharedBoxHash = await sha256(JSON.stringify(newSharedBox));
    mergedSharedBox.hash = newSharedBoxHash;
    return mergedSharedBox;
}

export const GetAttachmentLink = (attachment: SharedBoxAttachment) => {
    const endpoint = process.env.REACT_APP_API_URL;
    const jwt = store.getState().userSession.sessionToken;
    return `${endpoint}/api/v1/external/sharedbox/attachment/${attachment.id}?ujwt=${jwt}`;
}